import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const REACT_APP_URL = process.env.REACT_APP_URL || "";
const REACT_APP_IMAGE_URL = process.env.REACT_APP_IMAGE_URL || "";

function joinUrl(base, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const b = (base || "").replace(/\/+$/, "");
  const p = String(path).startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// -----------------------------
// Expanded RFQ detail panel
// -----------------------------
const Field = ({ label, value, children }) => (
  <div>
    <div className="al-th" style={{ marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", wordBreak: "break-word" }}>
      {children || value || "—"}
    </div>
  </div>
);

const DetailBlock = ({ title, children }) => (
  <div className="al-subpanel" style={{ marginBottom: 12, background: "var(--color-surface)" }}>
    <div className="al-subpanel__head" style={{ marginBottom: 12 }}>
      <span className="al-subpanel__title">{title}</span>
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 14,
      }}
    >
      {children}
    </div>
  </div>
);

const RFQDetails = ({ item }) => {
  const dims = useMemo(
    () => `${item.length || "-"} × ${item.width || "-"} × ${item.height || "-"}`,
    [item.length, item.width, item.height]
  );
  const logoUrl = joinUrl(REACT_APP_IMAGE_URL, item.logo);
  const refUrl = joinUrl(REACT_APP_IMAGE_URL, item.reference_image);

  return (
    <div style={{ padding: "4px 18px 18px" }}>
      <DetailBlock title="1. Basic Info">
        <Field label="Company" value={item.company} />
        <Field label="Name" value={item.name} />
        <Field label="Email" value={item.email} />
        <Field label="Phone" value={item.contact} />
        <Field label="Industry" value={item.industry} />
        <Field label="Buyer Category" value={item.buyer_category} />
      </DetailBlock>

      <DetailBlock title="2. Packaging Requirement">
        <Field label="Packaging Type" value={item.packaging_type} />
        <Field label="Product" value={item.product_name} />
        <Field label="Dimensions (L×W×H)" value={dims} />
        <Field label="Frequency" value={item.frequency} />
        <Field label="Material" value={item.material} />
        <Field label="Paper GSM" value={item.paper_gsm} />
      </DetailBlock>

      <DetailBlock title="3. Brand & Design">
        <Field label="Style Preference" value={item.style_preference} />
        <Field label="Brand Colors" value={item.brand_colors} />
        <Field label="Print Model" value={item.print_model} />
        {logoUrl && (
          <Field label="Logo">
            <a href={logoUrl} target="_blank" rel="noreferrer">
              <img
                src={logoUrl}
                alt="logo"
                style={{ width: 90, height: 90, borderRadius: 10, objectFit: "cover", border: "1px solid var(--color-border)" }}
              />
            </a>
          </Field>
        )}
      </DetailBlock>

      <DetailBlock title="4. Files">
        {refUrl ? (
          <Field label="Reference Image">
            <a href={refUrl} target="_blank" rel="noreferrer">
              <img
                src={refUrl}
                alt="reference"
                style={{ width: 120, height: 120, borderRadius: 10, objectFit: "cover", border: "1px solid var(--color-border)" }}
              />
            </a>
          </Field>
        ) : (
          <span className="al-muted">No files uploaded</span>
        )}
      </DetailBlock>

      <div className="al-foot__info" style={{ textAlign: "right" }}>
        Created: {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
      </div>
    </div>
  );
};

// -----------------------------
// Main Component
// -----------------------------
const GRID = {
  gridTemplateColumns:
    "64px 1.3fr 1.7fr 1.2fr 1fr .9fr .9fr .9fr 84px",
};

const Contacts = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const alertedRef = useRef(false);

  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dense, setDense] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch RFQ list
  const fetchContacts = (pageNo = page) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pageNo, limit: 10 });
    if (search) params.append("search", search);

    axios
      .get(`${REACT_APP_URL}/rfq?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
      })
      .then((res) => {
        const list = res?.data?.data?.data || [];
        const tp = res?.data?.data?.pagination?.totalPages || 1;
        setData(list);
        setTotalPages(tp);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          localStorage.removeItem("Token");
          Navigate("/admin/login");
        } else toast.error("Failed to load RFQs");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchContacts(page);
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchContacts(1);
  }, [search]);

  const toggleRow = (id) => setExpanded((cur) => (cur === id ? null : id));

  // -----------------------------
  // Export to Excel
  // -----------------------------
  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("page", 1);
      params.append("limit", 10000);

      const response = await axios.get(
        `${REACT_APP_URL}/rfq?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const exportData = response?.data?.data?.data || [];
      if (exportData.length === 0) {
        toast("No data to export.", { icon: "⚠️" });
        return;
      }

      const formatted = exportData.map((rfq, i) => ({
        "Sr. No.": i + 1,
        "RFQ ID": rfq.rfq_id,
        Name: rfq.name,
        Email: rfq.email,
        Contact: rfq.contact,
        Company: rfq.company,
        Industry: rfq.industry,
        "Buyer Category": rfq.buyer_category,
        "Packaging Type": rfq.packaging_type,
        Product: rfq.product_name,
        Length: rfq.length,
        Width: rfq.width,
        Height: rfq.height,
        Material: rfq.material,
        Frequency: rfq.frequency,
        "Paper GSM": rfq.paper_gsm,
        "Burst Factor": rfq.burst_factor,
        "Style Preference": rfq.style_preference,
        "Brand Colors": rfq.brand_colors,
        "Print Model": rfq.print_model,
        "Spot Colors": rfq.spot_colors,
        UV: rfq.uv ? "Yes" : "No",
        Lamination: rfq.lamination ? "Yes" : "No",
        Matte: rfq.matte ? "Yes" : "No",
        Gloss: rfq.gloss ? "Yes" : "No",
        Foil: rfq.foil ? "Yes" : "No",
        "Food Grade": rfq.food_grade_materials ? "Yes" : "No",
        "Direct Food Contact": rfq.direct_food_contact ? "Yes" : "No",
        FSSAI: rfq.fssai_documentation ? "Yes" : "No",
        "ISO 9001": rfq.iso_9001 ? "Yes" : "No",
        "Recycled Contact": rfq.recycled_contact ? "Yes" : "No",
        "FSC Paper": rfq.fsc_paper ? "Yes" : "No",
        "Plastic Free": rfq.plastic_free ? "Yes" : "No",
        "Compliance Notes": rfq.compliance_notes,
        "Budget Range": rfq.budget_range,
        "Desired Timeline": rfq.desired_timeline,
        Address: rfq.address,
        City: rfq.city,
        State: rfq.state,
        Country: rfq.country,
        Pincode: rfq.pincode,
        "Split Shipments": rfq.split_shipments ? "Yes" : "No",
        "Number of Splits": rfq.number_of_splits,
        GSTIN: rfq.gstin,
        "Created At": new Date(rfq.created_at).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));

      const header = Object.keys(formatted[0]);
      const ws = XLSX.utils.json_to_sheet(formatted, {
        skipHeader: true,
        origin: "A2",
      });
      XLSX.utils.sheet_add_aoa(ws, [header], { origin: "A1" });
      ws["!cols"] = header.map((h) => ({ wch: Math.max(h.length + 4, 15) }));

      header.forEach((col, i) => {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: i })];
        if (cell)
          cell.s = {
            fill: { fgColor: { rgb: "DCE6F1" } },
            font: { bold: true, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
          };
      });

      formatted.forEach((_, r) => {
        if (r % 2 === 1)
          header.forEach((_, c) => {
            const cell = ws[XLSX.utils.encode_cell({ r: r + 1, c })];
            if (cell)
              cell.s = {
                fill: { fgColor: { rgb: "F9F9F9" } },
                alignment: { horizontal: "left", vertical: "center" },
              };
          });
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RFQ Master List");

      const buffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(blob, "rfq-master-list.xlsx");
      toast.success("RFQs exported successfully!");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export RFQ data.");
    }
  };

  const EyeIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
  );
  const ChevronUp = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
  );

  return (
    <div className="al">
      <Toaster position="top-center" />

      <div className="al-head">
        <div>
          <h1 className="al-title">Contact RFQs</h1>
          <p className="al-sub">Requests for quotation submitted via contact forms.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn al-btn--primary" onClick={exportToExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            Export Excel
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            <input
              type="search"
              placeholder="Search RFQs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchContacts(1)}
            />
          </div>
          <button
            type="button"
            className="al-tool"
            style={{ marginLeft: "auto" }}
            onClick={() => setDense((d) => !d)}
            title="Toggle density"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        {loading ? (
          <div className="al-empty">
            <span className="spinner-border spinner-border-sm text-secondary" role="status" />
            <p className="al-empty__text" style={{ marginTop: 10 }}>Loading RFQs…</p>
          </div>
        ) : !isMobileView ? (
          <div className="al-tablewrap">
            <div style={{ minWidth: 1180, "--al-row-pad": dense ? "8px" : "13px" }}>
              <div className="al-thead" style={GRID}>
                <span className="al-th">#</span>
                <span className="al-th">Name</span>
                <span className="al-th">Email</span>
                <span className="al-th">Company</span>
                <span className="al-th">Packaging Type</span>
                <span className="al-th">City</span>
                <span className="al-th">State</span>
                <span className="al-th">Created</span>
                <span className="al-th al-th--right">Action</span>
              </div>

              {data.length > 0 ? (
                data.map((item, index) => {
                  const open = expanded === item.rfq_id;
                  return (
                    <React.Fragment key={item.rfq_id}>
                      <div className="al-row" style={GRID}>
                        <span className="al-muted al-mono">
                          {(page - 1) * 10 + (index + 1)}
                        </span>
                        <span className="al-name">{item.name || "—"}</span>
                        <span className="al-muted al-ellipsis" title={item.email}>
                          {item.email || "—"}
                        </span>
                        <span className="al-muted al-ellipsis" title={item.company}>
                          {item.company || "—"}
                        </span>
                        <span className="al-muted al-ellipsis" title={item.packaging_type}>
                          {item.packaging_type || "—"}
                        </span>
                        <span className="al-muted al-ellipsis">{item.city || "—"}</span>
                        <span className="al-muted al-ellipsis">{item.state || "—"}</span>
                        <span className="al-mono al-muted">{formatDate(item.created_at)}</span>
                        <span className="al-actions">
                          <button
                            type="button"
                            className={`al-act ${open ? "" : "al-act--primary"}`}
                            title={open ? "Hide details" : "View details"}
                            onClick={() => toggleRow(item.rfq_id)}
                          >
                            {open ? <ChevronUp /> : <EyeIcon />}
                          </button>
                        </span>
                      </div>
                      {open && (
                        <div
                          style={{
                            borderBottom: "1px solid var(--color-border-soft)",
                            background: "var(--color-surface-2)",
                          }}
                        >
                          <RFQDetails item={item} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <div className="al-empty">
                  <div className="al-empty__icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  </div>
                  <p className="al-empty__title">No RFQs Found</p>
                  <p className="al-empty__text">Contact-form RFQs will appear here.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="al-mobile">
            {data.length > 0 ? (
              data.map((item, index) => {
                const open = expanded === item.rfq_id;
                return (
                  <div className="al-mcard" key={item.rfq_id}>
                    <div className="al-mcard__top">
                      <div className="al-name" style={{ flex: 1 }}>
                        {item.name || "—"}
                      </div>
                      <button
                        type="button"
                        className={`al-act ${open ? "" : "al-act--primary"}`}
                        title={open ? "Hide details" : "View details"}
                        onClick={() => toggleRow(item.rfq_id)}
                      >
                        {open ? <ChevronUp /> : <EyeIcon />}
                      </button>
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Email</span>
                      <span className="al-ellipsis" style={{ textAlign: "right" }}>
                        {item.email || "—"}
                      </span>
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Company</span>
                      <span>{item.company || "—"}</span>
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Packaging</span>
                      <span>{item.packaging_type || "—"}</span>
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Location</span>
                      <span>
                        {[item.city, item.state].filter(Boolean).join(", ") || "—"}
                      </span>
                    </div>
                    <div className="al-mrow">
                      <span className="al-muted">Created</span>
                      <span className="al-mono al-muted">{formatDate(item.created_at)}</span>
                    </div>
                    {open && <RFQDetails item={item} />}
                  </div>
                );
              })
            ) : (
              <div className="al-empty">
                <p className="al-empty__title">No RFQs Found</p>
              </div>
            )}
          </div>
        )}

        <div className="al-foot">
          <span className="al-foot__info">
            Showing <strong>{data?.length || 0}</strong> RFQs
          </span>
          {data.length > 0 && (
            <ReactPaginate
              previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>}
              nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>}
              breakLabel={"..."}
              pageCount={totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              forcePage={page - 1}
              onPageChange={(e) => setPage(e.selected + 1)}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              activeClassName={"active"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Contacts;
